import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAGES = [["draft", "Draft plan"], ["feedback", "Obtain feedback"], ["revised", "Revise plan"], ["implemented", "Implement plan"]];

export default function BehaviourPlan({ changeId, questions }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const [changes, setChanges] = useState([]);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/changes", { params: { limit: 200 } }).then((r) => setChanges(r.data.items.filter((c) => !["completed", "cancelled"].includes(c.status) || c.is_example))); }, []);
  useEffect(() => { if (changeId) api.get(`/changes/${changeId}/behaviour-plan`).then((r) => setPlan(r.data)); else setPlan(null); }, [changeId]);
  const current = changes.find((c) => c.id === changeId);
  const readOnly = !can("write") || current?.is_example;
  const save = async (stage) => {
    setBusy(true);
    try { const { data } = await api.put(`/changes/${changeId}/behaviour-plan`, { answers: plan.answers, stage: stage || plan.stage }); setPlan(data); toast.success("Plan saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="behaviour-plan" className="pp-card p-6 border-ink">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-head text-xl font-extrabold tracking-tight">The eight questions</h2>
        <select data-testid="bp-change" className="pp-input w-auto max-w-xs" value={changeId || ""} onChange={(e) => nav(`/app/learn/behavioural-change-plan${e.target.value ? `?change=${e.target.value}` : ""}`)}>
          <option value="">Pick a change to fill this in for…</option>
          {changes.map((c) => <option key={c.id} value={c.id}>{`${c.ref} · ${c.title}`}</option>)}
        </select>
      </div>
      {!changeId && <p className="text-sm text-muted-foreground mt-3">Answers are saved per change, so pick one first. Reading the questions without a change in mind is allowed, but less useful.</p>}
      {changeId && plan && (
        <div className="mt-5 space-y-5">
          <div className="flex gap-1 bg-secondary rounded-full p-1 text-xs font-bold w-fit" data-testid="bp-stage">{STAGES.map(([k, l]) => <button key={k} type="button" disabled={readOnly} data-testid={`bp-stage-${k}`} onClick={() => save(k)} className={`rounded-full px-3 py-1.5 transition-colors ${plan.stage === k ? "bg-ink text-on-dark" : "text-subtle"}`}>{l}</button>)}</div>
          {questions.map((q, i) => (
            <div key={i}>
              <label className="text-sm font-bold text-foreground">{i + 1}. {q}</label>
              <textarea data-testid={`bp-answer-${i}`} readOnly={readOnly} className="pp-input mt-1.5 min-h-[72px]" value={plan.answers[i] || ""} onChange={(e) => setPlan({ ...plan, answers: plan.answers.map((a, j) => (j === i ? e.target.value : a)) })} />
            </div>
          ))}
          {!readOnly && <div className="flex justify-end"><button data-testid="bp-save" disabled={busy} onClick={() => save()} className="pp-btn-dark text-sm py-2">{busy ? "Saving…" : "Save plan"}</button></div>}
          {current?.is_example && <p className="text-xs text-muted-foreground">Worked example — read-only.</p>}
        </div>
      )}
    </section>
  );
}
